import { useState, useMemo } from 'react'
import { History, RefreshCw } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useRevisions } from '@/hooks/api/useRevisions'
import { useUsers } from '@/hooks/api'
import { LoadingSpinner, EmptyState } from '@/components/common'
import { Button } from '@/components/base/buttons'
import type { Revision } from '@/types/revision'
import { RevisionItem } from './RevisionItem'
import { RevisionCompareDrawer } from './RevisionCompareDrawer'

interface RevisionsListProps {
  /** The resource type (e.g., 'assistants') */
  resource: string
  /** The record ID to fetch revisions for */
  recordId: number
}

/**
 * Displays a timeline list of all revisions for a record
 */
export function RevisionsList({ resource, recordId }: RevisionsListProps) {
  const { t } = useTranslation()
  const { getValues } = useFormContext()
  const [selectedRevision, setSelectedRevision] = useState<Revision | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Fetch revisions
  const {
    data: revisions,
    isLoading,
    isError,
    refetch,
  } = useRevisions(resource, recordId)

  // Fetch users to get author names
  // This gets all users - in a real app you might want to filter or use a different approach
  const { data: users = [] } = useUsers()

  // Create a map of user IDs to names for quick lookup
  const authorMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const user of users) {
      const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
      map.set(user.id, name)
    }
    return map
  }, [users])

  // Get current form data for comparison
  const currentData = getValues() as Record<string, unknown>

  // Handle compare click
  const handleCompare = (revision: Revision) => {
    setSelectedRevision(revision)
    setIsDrawerOpen(true)
  }

  // Handle drawer close
  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedRevision(null)
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          {t('resources.assistants.revisions.load_error')}
        </p>
        <Button color="secondary" onPress={() => refetch()} className="mt-4" iconLeading={RefreshCw}>
          {t('resources.assistants.revisions.retry')}
        </Button>
      </div>
    )
  }

  // Empty state
  if (!revisions || revisions.length === 0) {
    return (
      <EmptyState
        icon={<History className="h-12 w-12 text-gray-400" />}
        title={t('resources.assistants.revisions.no_revisions')}
        description={t('resources.assistants.revisions.no_revisions_description')}
      />
    )
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('resources.assistants.revisions.revision_history')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.revisions.recorded_count', { count: revisions.length })}
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {revisions.map((revision, index) => (
          <RevisionItem
            key={revision.id}
            revision={revision}
            isFirst={index === 0}
            isLast={index === revisions.length - 1}
            authorName={authorMap.get(revision.authorId)}
            onCompare={handleCompare}
          />
        ))}
      </div>

      {/* Compare drawer */}
      <RevisionCompareDrawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        revision={selectedRevision}
        currentData={currentData}
        authorName={
          selectedRevision ? authorMap.get(selectedRevision.authorId) : undefined
        }
      />
    </>
  )
}

export default RevisionsList
